import React from 'react';
import { Select, Input } from '@/shared/components/ui';

const RECIPIENT_FIELDS = [
  { value: 'name', label: 'Nome do Contato' },
  { value: 'phone', label: 'Telefone' },
  { value: 'email', label: 'Email' },
  { value: 'company', label: 'Empresa' },
  { value: 'city', label: 'Cidade' },
  { value: 'state', label: 'Estado' },
];

const SOURCE_OPTIONS = [
  { value: 'field', label: 'Campo do contato' },
  { value: 'static', label: 'Valor fixo' },
];

interface TemplateVariable {
  position: number;
  label: string;
  type: string;
  required: boolean;
  example: string;
  description?: string;
}

type VariableMappingValue = {
  source: 'field' | 'static';
  fieldName?: string;
  staticValue?: string;
};

export interface VariableMappingEditorProps {
  variables: TemplateVariable[];
  variableMapping: Record<number, VariableMappingValue>;
  onChange: (mapping: Record<number, VariableMappingValue>) => void;
  /**
   * Optional override for the field dropdown options. When provided, only
   * these fields are selectable (used for CUSTOM_AUDIENCE which only has `name`).
   */
  availableFields?: string[];
}

export const VariableMappingEditor: React.FC<VariableMappingEditorProps> = ({
  variables,
  variableMapping,
  onChange,
  availableFields,
}) => {
  const recipientFields = availableFields
    ? RECIPIENT_FIELDS.filter((f) => availableFields.includes(f.value))
    : RECIPIENT_FIELDS;
  if (!variables || variables.length === 0) {
    return (
      <p className="text-sm py-2" style={{ color: 'var(--theme-muted-text)' }}>
        Este template nao possui variaveis.
      </p>
    );
  }

  const updateMapping = (position: number, updates: Partial<VariableMappingValue>) => {
    const current = variableMapping[position] ?? { source: 'field' };
    onChange({
      ...variableMapping,
      [position]: { ...current, ...updates },
    });
  };

  return (
    <div className="space-y-3">
      {variables.map((variable) => {
        const mapping = variableMapping[variable.position] ?? { source: 'field' };

        return (
          <div
            key={variable.position}
            className="rounded-lg border overflow-hidden"
            style={{ borderColor: 'var(--theme-card-border)' }}
          >
            {/* Header */}
            <div
              className="flex items-center gap-3 px-4 py-2.5"
              style={{
                backgroundColor: 'var(--theme-button-ghost-hover)',
                borderBottom: '1px solid var(--theme-card-border)',
              }}
            >
              <span
                className="inline-flex items-center justify-center text-xs font-bold px-2 py-0.5 rounded"
                style={{
                  backgroundColor: 'var(--theme-primary)',
                  color: '#ffffff',
                  minWidth: '2.5rem',
                }}
              >
                {`{{${variable.position}}}`}
              </span>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span
                  className="text-sm font-medium truncate"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  {variable.label}
                </span>
                {variable.required && (
                  <span className="text-xs" style={{ color: 'var(--theme-error-color)' }}>
                    *
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span
                  className="text-xs px-1.5 py-0.5 rounded"
                  style={{
                    backgroundColor: 'var(--theme-info-background)',
                    color: 'var(--theme-info-color)',
                  }}
                >
                  {variable.type}
                </span>
                {variable.example && (
                  <span
                    className="text-xs hidden sm:inline"
                    style={{ color: 'var(--theme-muted-text)' }}
                  >
                    ex: {variable.example}
                  </span>
                )}
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-start gap-3 px-4 py-3">
              {/* Source selector */}
              <div className="w-44 flex-shrink-0">
                <Select
                  options={SOURCE_OPTIONS}
                  value={mapping.source}
                  onChange={(val) =>
                    updateMapping(variable.position, {
                      source: val as 'field' | 'static',
                      fieldName: undefined,
                      staticValue: undefined,
                    })
                  }
                  placeholder="Tipo de origem"
                />
              </div>

              {/* Value input */}
              <div className="flex-1 min-w-0">
                {mapping.source === 'field' ? (
                  <Select
                    options={recipientFields}
                    value={mapping.fieldName ?? ''}
                    onChange={(val) => updateMapping(variable.position, { fieldName: val })}
                    placeholder="Selecione o campo"
                  />
                ) : (
                  <Input
                    type="text"
                    value={mapping.staticValue ?? ''}
                    onChange={(e) =>
                      updateMapping(variable.position, { staticValue: e.target.value })
                    }
                    placeholder="Digite o valor fixo"
                  />
                )}
              </div>
            </div>

            {/* Description (optional) */}
            {variable.description && (
              <div
                className="px-4 pb-2.5"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                <p className="text-xs">{variable.description}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
